import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import {
  getClientAccessInfo,
  getClientComment,
  getClientPhotos,
} from './interventionClientContent';

const InterventionModal = ({ isOpen, onClose, intervention }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [appointmentResult, setAppointmentResult] = useState(null);

  useEffect(() => {
    if (intervention) {
      if (intervention.appointment_result) {
        try {
          const resultData = typeof intervention.appointment_result === 'string' 
            ? JSON.parse(intervention.appointment_result)
            : intervention.appointment_result;
          
          const result = Array.isArray(resultData) ? resultData[0] : resultData;
          setAppointmentResult(result);
          console.log('Appointment result chargé:', result);
        } catch (err) {
          console.error('Erreur lors du parsing de appointment_result:', err);
          setAppointmentResult(null);
        }
      } else {
        setAppointmentResult(null);
      }
    }
  }, [intervention]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    if (timeStr.includes(':')) {
      return timeStr.substring(0, 5);
    }
    return timeStr;
  };

  const formatPrice = (price) => {
    const numPrice = parseFloat(price) || 0;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(numPrice);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'scheduled': 'bg-blue-100 text-blue-700',
      'planned': 'bg-blue-100 text-blue-700',
      'on_site': 'bg-green-100 text-green-700',
      'in_progress': 'bg-yellow-100 text-yellow-700',
      'completed': 'bg-purple-100 text-purple-700',
      'cancelled': 'bg-red-100 text-red-700',
      'pending': 'bg-orange-100 text-orange-700'
    };

    const labels = {
      'scheduled': 'Planifié',
      'planned': 'Planifié',
      'on_site': 'Sur place',
      'in_progress': 'En cours',
      'completed': 'Terminé',
      'cancelled': 'Annulé',
      'pending': 'En attente'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  
  const getUrgencyBadge = (urgency) => {
    const styles = {
      'today': 'bg-red-100 text-red-700',
      'urgent': 'bg-orange-100 text-orange-700',
      'standard': 'bg-blue-100 text-blue-700',
      'high': 'bg-red-100 text-red-700',
      'medium': 'bg-orange-100 text-orange-700',
      'low': 'bg-green-100 text-green-700'
    };

    const labels = {
      'today': 'Aujourd\'hui',
      'within-hour': 'Sous 2h',
      'urgent': 'Urgent',
      'standard': 'Standard',
      'high': 'Haute',
      'medium': 'Moyenne',
      'low': 'Basse'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${styles[urgency] || 'bg-gray-100 text-gray-700'}`}>
        {labels[urgency] || urgency}
      </span>
    );
  };

  if (!isOpen || !intervention) return null;

  const checklist = appointmentResult?.checklist || [];
  const imagesData = appointmentResult?.image?.[0];
  const beforeImages = imagesData?.before || [];
  const afterImages = imagesData?.after || [];
  const signature = appointmentResult?.signature;

  const clientPhotos = getClientPhotos(intervention);
  const clientComment = getClientComment(intervention);
  const clientAccessInfo = getClientAccessInfo(intervention);

  const hasChecklist = checklist.length > 0;
  const hasTechnicianImages = beforeImages.length > 0 || afterImages.length > 0;
  const hasClientPhotos = clientPhotos.length > 0;
  const hasClientComments = Boolean(clientComment || clientAccessInfo);
  const hasSignature = !!signature;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      <div className="relative h-[107vh] flex items-center justify-center p-4">
        <div className="relative  bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <Icon name="Info" size={24} className="text-primary" />
              <h2 className="text-xl font-semibold text-gray-900">
                Détails de l'intervention
              </h2>
              {intervention && (
                <span className="text-sm text-gray-500 font-mono">
                  {intervention.reference}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Informations générales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 mb-3">Informations générales</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs text-gray-400">Référence</span>
                    <p className="text-sm font-mono text-gray-900">{intervention.reference}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Date & Heure</span>
                    <p className="text-sm text-gray-900">
                      {formatDate(intervention.scheduled_date)} à {formatTime(intervention.scheduled_time_start)} - {formatTime(intervention.scheduled_time_end)}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Statut</span>
                    <div className="mt-1">{getStatusBadge(intervention.status)}</div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Urgence</span>
                    <div className="mt-1">{getUrgencyBadge(intervention.urgency)}</div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 mb-3">Client</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs text-gray-400">Nom</span>
                    <p className="text-sm font-medium text-gray-900">{intervention.customer_name}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Email</span>
                    <p className="text-sm text-gray-600">{intervention.customer_email || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Téléphone</span>
                    <p className="text-sm text-gray-600">{intervention.customer_phone || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Adresse</span>
                    <p className="text-sm text-gray-600">{intervention.customer_address || 'Non spécifiée'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 mb-3">Service</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs text-gray-400">Type de service</span>
                    <p className="text-sm text-gray-900">{intervention.service_type}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 mb-3">Technicien</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs text-gray-400">Technicien assigné</span>
                    <p className="text-sm text-gray-900">{intervention.technician || 'Non assigné'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Heure d'arrivée réelle</span>
                    <p className="text-sm text-gray-600">{intervention.actual_arrival_time || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">Heure de fin</span>
                    <p className="text-sm text-gray-600">{intervention.completion_time || 'Non renseigné'}</p>
                  </div>
                </div>
              </div>
            </div>

            {(hasClientComments || hasClientPhotos) && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Icon name="MessageSquare" size={20} className="mr-2 text-primary" />
                  Informations client (réservation)
                </h3>

                {hasClientComments && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                    <div>
                      <span className="text-xs text-gray-400">Commentaire</span>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {clientComment || 'Aucun commentaire'}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400">Informations d&apos;accès</span>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {clientAccessInfo || 'Non renseigné'}
                      </p>
                    </div>
                  </div>
                )}

                {hasClientPhotos && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <Icon name="Camera" size={16} className="mr-2 text-primary" />
                      Photos transmises par le client ({clientPhotos.length})
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {clientPhotos.map((img, idx) => (
                        <button
                          key={`${img}-${idx}`}
                          type="button"
                          className="block text-left"
                          onClick={() => window.open(img, '_blank')}
                        >
                          <img
                            src={img}
                            alt={`Photo client ${idx + 1}`}
                            className="w-full h-40 object-cover rounded-lg border border-gray-200 hover:shadow-lg transition-shadow"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/400x300?text=Image+non+chargée';
                            }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4 mb-8">
              <h3 className="text-sm font-medium text-gray-500 mb-3">Informations financières</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <span className="text-xs text-gray-400">Sous-total HT</span>
                  <p className="text-sm font-medium text-gray-900">{formatPrice(intervention.price_subtotal)}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">TVA</span>
                  <p className="text-sm font-medium text-gray-900">{formatPrice(intervention.price_vat)}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Total TTC</span>
                  <p className="text-sm font-bold text-primary">{formatPrice(intervention.price_total)}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Statut paiement</span>
                  <p className={`text-sm font-medium ${intervention.is_quote ? 'text-blue-600' : intervention.payment_status === 'succeeded' ? 'text-green-600' : 'text-red-600'}`}>
                    {intervention.is_quote ? 'Sur Devis' : intervention.payment_status === 'succeeded' ? 'Payé' : 'Non payé'}
                  </p>
                </div>
              </div>
            </div>

            {/* Section Checklist */}
            {hasChecklist && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Icon name="CheckSquare" size={20} className="mr-2 text-primary" />
                  Checklist
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-green-800">
                          Progression
                        </span>
                        <span className="text-sm font-bold text-green-800">
                          {checklist.filter(item => item.checked).length} / {checklist.length}
                        </span>
                      </div>
                      <div className="mt-2 w-full bg-green-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 rounded-full h-2 transition-all duration-300"
                          style={{ width: `${(checklist.filter(item => item.checked).length / checklist.length) * 100}%` }}
                        />
                      </div>
                    </div>

                    {checklist.map((item, index) => (
                      <div 
                        key={item.id || index}
                        className={`flex items-start space-x-3 p-3 rounded-lg border ${
                          item.checked ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {item.checked ? (
                            <Icon name="CheckCircle" size={20} className="text-green-600" />
                          ) : (
                            <Icon name="Circle" size={20} className="text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <p className={`text-sm font-medium ${item.checked ? 'text-green-800' : 'text-gray-700'}`}>
                              {item.label}
                            </p>
                            {item.required && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                                Requis
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Section Images technicien */}
            {hasTechnicianImages && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Icon name="Image" size={20} className="mr-2 text-primary" />
                  Photos d&apos;intervention (technicien)
                </h3>
                <div className="space-y-6">
                  {/* Avant images */}
                  {beforeImages.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                        <Icon name="Camera" size={16} className="mr-2 text-orange-500" />
                        Avant intervention ({beforeImages.length})
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {beforeImages.map((img, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={img} 
                              alt={`Avant intervention ${idx + 1}`}
                              className=" h-48 object-cover rounded-lg border border-gray-200 cursor-pointer hover:shadow-lg transition-shadow"
                              onClick={() => window.open(img, '_blank')}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/400x300?text=Image+non+chargée';
                              }}
                            />
                           
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Après images */}
                  {afterImages.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                        <Icon name="CheckCircle" size={16} className="mr-2 text-green-500" />
                        Après intervention ({afterImages.length})
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {afterImages.map((img, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={img} 
                              alt={`Après intervention ${idx + 1}`}
                              className=" h-48 object-cover rounded-lg border border-gray-200 cursor-pointer hover:shadow-lg transition-shadow"
                              onClick={() => window.open(img, '_blank')}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/400x300?text=Image+non+chargée';
                              }}
                            />
                            
                             
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Section Signature */}
            {hasSignature && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Icon name="PenTool" size={20} className="mr-2 text-primary" />
                  Signature du client
                </h3>
                   <div className="flex justify-center">
                    <img 
                      src={signature} 
                      alt="Signature du client"
                      className="   border border-gray-300 rounded-lg shadow-sm"
                       
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/400x200?text=Signature+non+chargée';
                      }}
                    />
                  
                </div>
              </div>
            )}

            {/* Message si aucune donnée supplémentaire */}
            {!hasChecklist && !hasTechnicianImages && !hasClientPhotos && !hasClientComments && !hasSignature && (
              <div className="text-center py-8 bg-gray-50 rounded-lg mb-8">
                <Icon name="Info" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Aucune information complémentaire disponible</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <Button variant="outline" onClick={onClose}>
                Fermer
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterventionModal;